import { FileText, Plus } from "lucide-react";
import type { Business } from "@/features/business/types/businessTypes";
import { Button } from "@/shared/components/shadcn/Button";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { IconBadge } from "@/shared/components/ui/IconBadge";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Section } from "@/shared/components/ui/Section";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { useForms } from "../hooks/formQueries";

export function BusinessForms({ business }: { business: Business }) {
  const forms = useForms(business.id);
  const { goToFormCreate, goToFormDetails, goToFormEdit } = useAppNavigation();
  return (
    <Section title={business.name}
      count={forms.data && !forms.error ? `${forms.data.length} ${forms.data.length === 1 ? "form" : "forms"}` : undefined}
      action={<Button onClick={() => goToFormCreate(business.id)} aria-label={`Create form for ${business.name}`}><Plus aria-hidden="true" />Create</Button>}>
      {forms.isLoading ? <LoadingSpinner message="Loading forms..." />
        : forms.error ? <ErrorState message={forms.error.message} onRetry={() => void forms.refetch()} />
        : !forms.data?.length ? <EmptyState title="No forms yet" message="Create your first form with a name and a raw definition. Every save keeps a new version." />
        : <ul className="grid min-w-0 gap-[9px]">
          {forms.data.map(form => (
            <li key={form.id} className="flex min-w-0 items-center rounded-2xl border border-border bg-card text-card-foreground">
              <button type="button" onClick={() => goToFormDetails(business.id, form.id)}
                aria-label={`View ${form.name}`}
                className="flex min-h-[82px] min-w-0 flex-1 items-center gap-3 rounded-[inherit] px-3 py-3 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:px-[18px] sm:py-3.5">
                <IconBadge tone="primary"><FileText /></IconBadge>
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-semibold leading-snug [overflow-wrap:anywhere]">{form.name}</span>
                  <span className="mt-1 block text-xs text-muted-foreground">Version {form.version}</span>
                </span>
              </button>
              <Button variant="ghost" className="mr-2 shrink-0 text-primary" aria-label={`Edit ${form.name}`}
                onClick={() => goToFormEdit(business.id, form.id)}>Edit</Button>
            </li>
          ))}
        </ul>}
    </Section>
  );
}
